import { authFetch } from '../auth/authFetch';
import { peekWorkspaceIdentity, redirectToSignIn, requireIdentityHeaders } from '../auth/identity';
import { WORKSPACE_IDS } from '../auth/workspaces';

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'
).replace(/\/$/, '');

function buildQueryString(query) {
  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.set(key, String(value));
    }
  });
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

function extractErrorMessage(errorPayload, status) {
  const fallback = `Request failed (${status})`;
  if (!errorPayload || typeof errorPayload !== 'object') {
    return fallback;
  }
  if (typeof errorPayload.message === 'string' && errorPayload.message.trim()) {
    return errorPayload.message;
  }
  if (typeof errorPayload.detail === 'string' && errorPayload.detail.trim()) {
    return errorPayload.detail;
  }
  return fallback;
}

function getContextHeaders() {
  return requireIdentityHeaders(WORKSPACE_IDS.PROVIDER);
}

async function parseResponse(response) {
  if (!response.ok) {
    if (response.status === 401) redirectToSignIn();
    let message = `Request failed (${response.status})`;
    try {
      const errorPayload = await response.json();
      message = extractErrorMessage(errorPayload, response.status);
    } catch {
      // keep default
    }
    const error = new Error(message);
    error.status = response.status;
    throw error;
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

async function request(path, { method = 'GET', body, query } = {}) {
  const response = await authFetch(`${API_BASE_URL}${path}${buildQueryString(query)}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...getContextHeaders(),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  return parseResponse(response);
}

export function getProviderAdminApiConfig() {
  const identity = peekWorkspaceIdentity(WORKSPACE_IDS.PROVIDER);
  return {
    apiBaseUrl: API_BASE_URL,
    userId: identity.userId,
    orgId: identity.orgId,
  };
}

export function listOrganizations(query) {
  return request('/v1/provider/admin/organizations', { query });
}

export function getOrganization(orgId) {
  return request(`/v1/provider/admin/organizations/${orgId}`);
}

export function getOrganizationIngestChannels(orgId) {
  return request(`/v1/provider/admin/organizations/${orgId}/ingest-channels`);
}

export function createOrganization(body) {
  return request('/v1/provider/admin/organizations', { method: 'POST', body });
}

export function updateOrganization(orgId, body) {
  return request(`/v1/provider/admin/organizations/${orgId}`, { method: 'PATCH', body });
}

export function deleteOrganization(orgId) {
  return request(`/v1/provider/admin/organizations/${orgId}`, { method: 'DELETE' });
}

export function listMembers(orgId) {
  return request(`/v1/provider/admin/organizations/${orgId}/members`);
}

export function createInvitation(orgId, body) {
  return request(`/v1/provider/admin/organizations/${orgId}/invitations`, {
    method: 'POST',
    body,
  });
}

export function listInvitations(orgId) {
  return request(`/v1/provider/admin/organizations/${orgId}/invitations`);
}

export function resendInvitation(orgId, invitationId) {
  return request(
    `/v1/provider/admin/organizations/${orgId}/invitations/${invitationId}/resend`,
    { method: 'POST' }
  );
}

export function revokeInvitation(orgId, invitationId) {
  return request(`/v1/provider/admin/organizations/${orgId}/invitations/${invitationId}`, {
    method: 'DELETE',
  });
}

export function listPeople(query) {
  return request('/v1/provider/admin/people', { query });
}

/** Issue a one-time password reset link for a person in this Provider's
 *  scope. The link is emailed; `reset_url` only comes back when SMTP is not
 *  configured so the admin can pass it on out-of-band. */
export function sendPasswordReset(userId) {
  return request(`/v1/provider/admin/people/${userId}/password-reset`, { method: 'POST' });
}

export function listRoles(query) {
  return request('/v1/provider/admin/roles', { query });
}

export function listConnections() {
  return request('/v1/provider/admin/connections');
}

export function createConnection(body) {
  return request('/v1/provider/admin/connections', { method: 'POST', body });
}

export function deleteConnection(connectionId) {
  return request(`/v1/provider/admin/connections/${connectionId}`, { method: 'DELETE' });
}

export function listDigitizeBindings() {
  return request('/v1/provider/admin/digitize/bindings');
}

export function upsertDigitizeBinding(orgId, body) {
  return request(`/v1/provider/admin/digitize/bindings/${orgId}`, {
    method: 'PUT',
    body,
  });
}

export function listDigitizeQueues() {
  return request('/v1/provider/admin/digitize/queues');
}

export function listAudit(query) {
  return request('/v1/provider/admin/audit', { query });
}

export function listProviderPrograms({ page, pageSize } = {}) {
  return request('/v1/provider/admin/programs', {
    query: { page, page_size: pageSize },
  });
}

export function createProviderProgram(body) {
  return request('/v1/provider/admin/programs', { method: 'POST', body });
}

// Custody mandates (BRD EV-10 / EV-13): the authority under which a custodian
// accepts assets on a programme. Admission is refused without an ACTIVE one.
export function listProgramCustodyMandates(programId) {
  return request(`/v1/provider/admin/programs/${programId}/custody-mandates`);
}

export function createProgramCustodyMandate(programId, body) {
  return request(`/v1/provider/admin/programs/${programId}/custody-mandates`, {
    method: 'POST',
    body,
  });
}

export function activateProgramCustodyMandate(programId, mandateId) {
  return request(
    `/v1/provider/admin/programs/${programId}/custody-mandates/${mandateId}/activate`,
    { method: 'POST' }
  );
}

export function revokeProgramCustodyMandate(programId, mandateId, body = {}) {
  return request(
    `/v1/provider/admin/programs/${programId}/custody-mandates/${mandateId}/revoke`,
    { method: 'POST', body }
  );
}
